import { LinkDecorator } from '@/ui/atoms/link-decorator/LinkDecorator'
import { Panel } from '@/ui/atoms/panel/Panel'
import { ScrollArea } from '@/ui/atoms/scroll-area/ScrollArea'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/ui/atoms/table/Table'
import { Typography } from '@/ui/atoms/typography/Typography'
import { getStaggerStyle } from '@/ui/utils/staggeredEntrance'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Fragment, cloneElement, useMemo, useState } from 'react'
import { ColumnHeader } from './components/ColumnHeader'
import { ColumnDefinition, FilterOptions } from './types'
export interface RowClickOptions {
  destination: string
  external?: boolean
}
interface RowType {
  [k: string]: any
  rowClickOptions?: RowClickOptions
}

export interface DataTableProps<T extends RowType, F> {
  columnDef: {
    [key: string]: ColumnDefinition<T>
  }
  scroll?: {
    height: number
  }
  hideTableHeader?: boolean
  gridTemplateColumnsClassName?: string
  data: T[]
  footer?: React.ReactNode
  'data-testid'?: string
  variant?: 'isolated-pairs' | 'isolated-positions' | 'default'
  emptyStateText?: string
  searchOptions?: {
    filterColumnKey: string
    placeholder: string
  }
  withPanel?: boolean
  wrapperClassName?: string
  filter?: FilterOptions<T, F>
}

/**
 * @note: Using columnDef as a dependency for columns memoizer will cause the table to re-mount on every re-render.
 * If this is the problem, take care of memoizing columnDef outside of the component, so react knows that
 * passed object is the same between renders, which will prevent unnecessary re-mount.
 */
export function DataTable<T extends RowType, F>({
  columnDef,
  data,
  scroll,
  hideTableHeader = false,
  gridTemplateColumnsClassName,
  footer,
  'data-testid': dataTestId,
  variant = 'default',
  emptyStateText = 'No data to display',
  searchOptions,
  withPanel,
  wrapperClassName,
  filter,
}: DataTableProps<T, F>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [filterValue, setFilterValue] = useState<F | undefined>(filter?.initialState)

  const filteredData = useMemo(() => {
    if (!filter || filterValue === undefined || !filter.isActive(filterValue)) {
      return data
    }
    return data.filter((item) => filter.filterFn(item, filterValue))
  }, [data, filterValue, filter])

  const columns: ColumnDef<T>[] = useMemo(() => {
    return Object.keys(columnDef).map<ColumnDef<T>>((key) => {
      const definition = columnDef[key]
      return {
        accessorKey: key,
        sortingFn: definition?.sortingFn,
        filterFn: definition?.filterFn,
        header: ({ column }) => <ColumnHeader column={column} columnDefinition={definition} />,
        cell: ({ row }) => definition?.renderCell(row.original),
      }
    })
  }, [columnDef])
  const columnVisibility = useMemo(
    () => ({
      ...(searchOptions ? { [searchOptions.filterColumnKey]: false } : {}),
    }),
    [searchOptions],
  )

  const table = useReactTable({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
  })

  const Wrapper = scroll ? ScrollWrapperWithHeight({ height: scroll.height }) : Fragment
  const PanelWrapper = withPanel ? Panel.Wrapper : Fragment
  const FilterComponent = filter?.component

  return (
    <div className="w-full">
      <Wrapper>
        {(FilterComponent || searchOptions) && (
          <div className="mb-6 flex items-center justify-between gap-4">
            {FilterComponent &&
              cloneElement(FilterComponent, {
                value: filterValue,
                onChange: setFilterValue,
              })}

            {searchOptions && (
              <div className="ml-auto w-full sm:max-w-44">
                <SearchInput
                  value={(table.getColumn(searchOptions.filterColumnKey)?.getFilterValue() as string) ?? ''}
                  placeholder={searchOptions.placeholder}
                  onChange={(e) => {
                    table.getColumn(searchOptions.filterColumnKey)?.setFilterValue(e.target.value)
                  }}
                  className="py-4"
                />
              </div>
            )}
          </div>
        )}

        <PanelWrapper
          {...(withPanel ? { className: cn('flex flex-col gap-5 p-4 pt-2 lg:p-8', wrapperClassName) } : {})}
        >
          <Table data-testid={dataTestId}>
            {!hideTableHeader && (
              <TableHeader className="static">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className={cn(
                      'grid w-full gap-2 border-white/4 border-b',
                      {
                        'md:px-3': variant === 'isolated-positions' || variant === 'default',
                        'px-3': variant === 'isolated-pairs',
                      },
                      gridTemplateColumnsClassName,
                    )}
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'py-2 font-bold text-neutral-500 text-xs',
                            variant === 'isolated-pairs' && 'px-1',
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
            )}
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row, index) => (
                  <TableRowWithLink key={row.id} rowClickOptions={row.original.rowClickOptions}>
                    <TableRow
                      className={cn(
                        'grid w-full animate-stagger-in items-center gap-2 border-white/4 border-b',
                        variant === 'default' && 'md:px-3',
                        variant === 'isolated-pairs' && 'px-3',
                        variant === 'isolated-positions' && 'px-3',
                        gridTemplateColumnsClassName,
                      )}
                      style={getStaggerStyle(index)}
                      data-testid={testIds.component.DataTable.row(index)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className={cn(variant === 'isolated-pairs' && 'px-1 py-3')}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableRowWithLink>
                ))
              ) : (
                <TableRow className="mt-8">
                  <TableCell colSpan={columns.length} className="pt-8 text-center md:pt-8">
                    <Typography variant="prompt" className="text-base">
                      {emptyStateText}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {footer && <TableFooter>{footer}</TableFooter>}
          </Table>
        </PanelWrapper>
      </Wrapper>
    </div>
  )
}

interface TableRowWithLinkProps {
  children: React.ReactNode
  rowClickOptions?: RowClickOptions
}

function TableRowWithLink({ children, rowClickOptions }: TableRowWithLinkProps) {
  if (!rowClickOptions) {
    return <>{children}</>
  }
  const { destination, external } = rowClickOptions
  return (
    <LinkDecorator to={destination} external={external}>
      {children}
    </LinkDecorator>
  )
}

function ScrollWrapperWithHeight({ height }: { height: number }) {
  function ScrollWrapper({ children }: { children: React.ReactNode }) {
    return <ScrollArea style={{ height }}>{children}</ScrollArea>
  }

  return ScrollWrapper
}
